import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { formatDate, formatInt } from "@/lib/format";
import { Empty, PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { StripeLink } from "@/components/StripeLink";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { useContactNames } from "./useContactNames";

interface Row {
  contact_id: number;
  keap_contact_id?: number;
  subscription_id: number;
  plan: string;
  next_bill_date?: string | null;
  billing_enabled: boolean;
  status: string;
  note?: string;
  stripe_customer_id?: string;
  dashboard_url?: string;
  card?: string;
  keap_last4?: string[];
  keap_expiry?: string;
  renews_on_card?: string;
  test?: boolean;
}

interface Checklist {
  stripe_mode: string;
  stripe_account?: string;
  counts: Record<string, number>;
  rows: Row[];
  message: string;
}

type Variant = "success" | "warning" | "destructive" | "muted" | "default";

/** billing_stripe_club_cards statuses, in the order Brandon works through them. */
export const CLUB_CARD_STATUS: Record<string, { label: string; chip: string; variant: Variant; sub: string }> = {
  to_enter: { label: "To enter", chip: "to enter", variant: "warning", sub: "type the card from Keap in Stripe" },
  check: { label: "To check", chip: "to check", variant: "destructive", sub: "doesn't match Keap, or not on the subscription yet" },
  ready: { label: "Ready", chip: "ready", variant: "success", sub: "renewals charge this Stripe card" },
  no_customer: { label: "No Stripe customer", chip: "no Stripe customer", variant: "muted", sub: "Claude creates them first" },
};

/** Go-live: Brandon moves each Club member's card to Stripe by hand (billing_stripe_club_cards). The CRM never sees a card number. */
export function ClubCardsPage() {
  const [status, setStatus] = useState("");
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ["club-cards"],
    queryFn: () => api<Checklist>("/api/v1/billing/stripe/club-cards"),
    refetchInterval: 15_000,
  });
  const sync = useMutation({
    mutationFn: () => api<{ message: string }>("/api/v1/billing/stripe/club-cards/sync", { json: {} }),
    onSuccess: (r) => {
      toast(r.message || "Checked");
      void qc.invalidateQueries({ queryKey: ["club-cards"] });
    },
    onError: (e) => toast(e.message, "error"),
  });
  const r = q.data;
  const tests = (r?.rows ?? []).filter((x) => x.test);
  const members = (r?.rows ?? []).filter((x) => !x.test);
  const rows = members.filter((x) => !status || x.status === status);
  const name = useContactNames([...tests, ...rows].map((x) => x.contact_id));
  const testDone = tests.length > 0 && tests.every((x) => x.status === "ready");
  return (
    <div className="mx-auto max-w-[1200px] space-y-3">
      <PageHeader title="Club cards to enter" sub="Go-live: moving each Club member's card to Stripe by hand">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          For each member: open them in Stripe, choose Add card under Payment methods, type the card shown in Keap, and save. This page picks the card up within
          seconds, makes it the member's card for renewals, and checks the last 4 (and expiry) against what Keap shows. Card numbers never pass through the CRM.
          Updates every 15 seconds.
        </p>
      </PageHeader>
      {q.isLoading && <Skeleton className="h-40" />}
      {q.error && <p className="text-sm text-[var(--color-danger-fg)]">{q.error.message}</p>}
      {r && (
        <>
          <Card className="p-3">
            <h2 className="font-semibold">Step 1: test with your own card first</h2>
            <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-sm">
              <li>Open yourself in Stripe and add your own card (Add card; the CVC can stay blank).</li>
              <li>Claude charges it once off-session, a small amount, with your OK.</li>
              <li>Claude refunds it.</li>
              <li>Only then enter the members' cards below.</li>
            </ol>
            {tests.length === 0 ? (
              <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                Your own contact isn't set up for the test yet: Claude adds it with billing_stripe_club_customers (test_contact_ids).
              </p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm">
                {tests.map((x) => {
                  const s = CLUB_CARD_STATUS[x.status] ?? { label: x.status, chip: x.status, variant: "muted" as const };
                  return (
                    <li key={x.contact_id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-medium">{name(x.contact_id)}</span>
                      <Badge variant={s.variant}>{x.status === "ready" ? "Card saved" : s.chip}</Badge>
                      {x.card && <span className="text-xs">{x.card}</span>}
                      <StripeLink url={x.dashboard_url} showMode={false} className="text-sm" what="Your customer page" />
                      {x.note && <span className="w-full text-xs text-[var(--color-muted-foreground)]">{x.note}</span>}
                    </li>
                  );
                })}
              </ul>
            )}
            {!testDone && tests.length > 0 && (
              <p className="mt-2 text-xs text-[var(--color-warning-fg)]">Finish the test (charge and refund) before entering members' cards.</p>
            )}
          </Card>
          <h2 className="pt-1 font-semibold">Step 2: the members' cards</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(CLUB_CARD_STATUS).map(([k, s]) => (
              <Card key={k} className="p-3">
                <div className="text-sm font-medium">{s.label}</div>
                <div className="num text-2xl font-semibold">{formatInt(r.counts[k] ?? 0)}</div>
                <div className="text-xs text-[var(--color-muted-foreground)]">{s.sub}</div>
              </Card>
            ))}
          </div>
          <Card className="overflow-x-auto">
            <div className="flex flex-wrap items-center gap-2 border-b p-3">
              <Select className="h-8 w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                <option value="">Every member</option>
                {Object.entries(CLUB_CARD_STATUS).map(([k, s]) => (
                  <option key={k} value={k}>
                    {s.label}
                  </option>
                ))}
              </Select>
              <span className="text-xs text-[var(--color-muted-foreground)]">
                {formatInt(rows.length)} {rows.length === 1 ? "member" : "members"}
              </span>
              <Badge variant={r.stripe_mode === "live" ? "success" : "warning"} title="Which Stripe account the Open in Stripe links go to">
                Stripe {r.stripe_mode === "live" ? "live" : "test mode"}
              </Badge>
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => sync.mutate()} disabled={sync.isPending}>
                {sync.isPending ? "Checking Stripe…" : "Check Stripe now"}
              </Button>
            </div>
            {rows.length === 0 ? (
              <Empty title={members.length === 0 ? "No open Club subscriptions." : "No members match."} />
            ) : (
              <Table>
                <THead>
                  <tr>
                    <TH>Member</TH>
                    <TH>Stripe</TH>
                    <TH>Plan</TH>
                    <TH>Next bill</TH>
                    <TH>Status</TH>
                    <TH>Card in Stripe</TH>
                    <TH>Keap shows</TH>
                  </tr>
                </THead>
                <tbody>
                  {rows.map((x) => {
                    const s = CLUB_CARD_STATUS[x.status] ?? { label: x.status, chip: x.status, variant: "muted" as const };
                    return (
                      <TR key={x.contact_id}>
                        <TD className="max-w-[14rem]">
                          <Link to="/contacts/$contactId" params={{ contactId: String(x.contact_id) }} search={{ view: "billing" }} className="block truncate hover:underline">
                            {name(x.contact_id)}
                          </Link>
                          {x.keap_contact_id ? (
                            <span className="num block whitespace-nowrap text-xs text-[var(--color-muted-foreground)]">Keap #{x.keap_contact_id}</span>
                          ) : null}
                        </TD>
                        <TD className="whitespace-nowrap">
                          {x.dashboard_url ? <StripeLink url={x.dashboard_url} showMode={false} className="text-sm" what="This member" /> : "—"}
                        </TD>
                        <TD className="whitespace-nowrap text-xs">{x.plan}</TD>
                        <TD className="num whitespace-nowrap text-xs">{x.next_bill_date ? formatDate(x.next_bill_date) : "—"}</TD>
                        <TD className="min-w-[12rem] max-w-[18rem]">
                          <Badge variant={s.variant}>{s.chip}</Badge>
                          {x.note && <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{x.note}</div>}
                        </TD>
                        <TD className="whitespace-nowrap text-xs">
                          {x.card ?? "—"}
                          {x.renews_on_card && !x.card && <div className="text-[var(--color-muted-foreground)]">now renews on {x.renews_on_card}</div>}
                        </TD>
                        <TD className="whitespace-nowrap text-xs">
                          {x.keap_last4?.length ? `ending ${x.keap_last4.join(", ")}` : "—"}
                          {x.keap_expiry && <span className="text-[var(--color-muted-foreground)]"> (exp {x.keap_expiry})</span>}
                        </TD>
                      </TR>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
